import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useStory, useSeo, cmsMediaUrl } from "../lib/cms";
import { ChevronLeft } from "lucide-react";

const StoryDetailPage = () => {
  const { slug } = useParams();
  const { story, loading, error } = useStory(slug);

  useSeo({
    title: story?.seo_title || story?.title || "Verhaal — Maison Vellore",
    description: story?.seo_description || story?.summary,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-ink-950">
        <Header />
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-32 text-sm text-zinc-500">
          Verhaal laden...
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="min-h-screen bg-ink-950" data-testid="story-not-found">
        <Header />
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 text-center">
          <h1 className="font-display text-5xl text-white mb-4 tracking-tight">
            Verhaal niet gevonden
          </h1>
          <p className="text-sm text-zinc-400 font-body mb-8">
            Dit verhaal bestaat niet of is niet meer gepubliceerd.
          </p>
          <Link
            to="/verhalen"
            className="inline-block px-6 py-3 border border-champagne text-champagne hover:bg-champagne hover:text-ink-950 transition-colors text-[11px] tracking-[0.3em] uppercase"
          >
            Terug naar verhalen
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const heroImg = cmsMediaUrl(story.hero_image);

  return (
    <div data-testid="story-detail-page" className="min-h-screen bg-ink-950">
      <Header />
      <main>
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-12 pt-8">
          <Link
            to="/verhalen"
            data-testid="story-back"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-zinc-500 hover:text-champagne transition-colors font-body"
          >
            <ChevronLeft size={13} /> Terug naar verhalen
          </Link>
        </div>

        <article className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-12 py-12 md:py-16">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-4">
              {story.author || "Editorial"}
              {story.published_at && (
                <>
                  <span className="mx-2 text-zinc-700">·</span>
                  {new Date(story.published_at).toLocaleDateString("nl-NL", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </>
              )}
            </p>
            <h1
              data-testid="story-title"
              className="font-display text-4xl md:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]"
            >
              {story.title}
            </h1>
            {story.summary && (
              <p className="mt-8 max-w-2xl mx-auto text-lg text-zinc-300 font-body leading-relaxed italic">
                {story.summary}
              </p>
            )}
          </div>

          {heroImg && (
            <div className="mt-12 md:mt-16 max-w-5xl mx-auto aspect-[16/9] overflow-hidden bg-ink-900">
              <img
                src={heroImg}
                alt={story.hero_image?.alt_text || story.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="mt-12 md:mt-16 max-w-2xl mx-auto">
            <div className="prose-style text-base md:text-lg text-zinc-300 font-body leading-[1.85] whitespace-pre-line">
              {story.body}
            </div>
          </div>

          {story.gallery_images?.length > 0 && (
            <div className="mt-16 max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {story.gallery_images.map((m) => (
                <div key={m.id} className="aspect-[4/3] overflow-hidden bg-ink-900">
                  <img
                    src={cmsMediaUrl(m)}
                    alt={m.alt_text || ""}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          )}
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default StoryDetailPage;
